import React, { useState, useEffect } from "react";
import { useClaimDailyReward } from "@/hooks/use-currency";
import { Button } from "@/components/ui/button";
import { Gift, Gem, Coins, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import appSound from '@/services/app-sound-service';

export function DailyReward() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [nextRewardTime, setNextRewardTime] = useState<Date | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(1);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [nextReward, setNextReward] = useState<{
    amount: number;
    currencyType: string;
    day: number;
    description: string;
  } | null>(null);

  // Fetch the daily reward status when the component mounts or dialog opens
  const fetchDailyRewardStatus = async () => {
    try {
      setIsLoadingStatus(true);
      const response = await apiRequest('GET', '/api/currency/daily-rewards/status');
      const data = await response.json();
      
      if (data.nextReward) {
        setNextReward(data.nextReward);
      }
      
      if (data.streak !== undefined) {
        setCurrentStreak(data.streak);
        localStorage.setItem("currentDailyStreak", data.streak.toString());
      }
      
      setClaimed(data.hasClaimedToday ?? false);
      
      if (data.hasClaimedToday) {
        localStorage.setItem("lastDailyRewardClaim", new Date().toDateString());
      }
      
    } catch (error) {
      console.error('Error fetching daily reward status:', error);
      toast({
        title: "Error",
        description: "Failed to fetch daily reward status",
        variant: "destructive"
      });
    } finally {
      setIsLoadingStatus(false);
    }
  };

  // Fetch status when component mounts
  useEffect(() => {
    fetchDailyRewardStatus();
  }, []);

  // Check if the reward has been claimed today from localStorage
  useEffect(() => {
    const lastClaimedDate = localStorage.getItem("lastDailyRewardClaim");
    if (lastClaimedDate) {
      const today = new Date().toDateString();
      if (lastClaimedDate === today) {
        setClaimed(true);
      }
    }
    
    // Get stored streak from localStorage
    const savedStreak = localStorage.getItem("currentDailyStreak");
    if (savedStreak) {
      setCurrentStreak(parseInt(savedStreak, 10));
    }
  }, []);

  const { mutate: claimReward, isPending } = useClaimDailyReward();

  const handleClaimReward = () => {
    claimReward(undefined, {
      onSuccess: (data: any) => {
        setClaimed(true);

        // Save claimed status to localStorage with today's date
        localStorage.setItem("lastDailyRewardClaim", new Date().toDateString());

        // Handle different data structures
        let nextTime = null;
        let rewardAmount = 50;
        let rewardType = "regular";
        let streak = currentStreak;

        console.log("Daily reward response:", data);

        // Check for nextRewardTime in various formats
        if (data?.nextRewardTime) {
          nextTime = new Date(data.nextRewardTime);
        }

        // Extract reward amount from various possible formats - prioritize reward.amount
        if (data?.reward?.amount !== undefined) {
          rewardAmount = data.reward.amount;
        } else if (data?.amount !== undefined) {
          rewardAmount = data.amount;
        }

        // Extract reward type from various possible formats - prioritize reward.currencyType
        if (data?.reward?.currencyType) {
          rewardType = data.reward.currencyType;
        } else if (data?.reward?.type) {
          rewardType = data.reward.type;
        } else if (data?.currencyType) {
          rewardType = data.currencyType;
        } else if (data?.type) {
          rewardType = data.type;
        }

        // Extract streak data
        if (data?.streak !== undefined) {
          streak = data.streak;
          setCurrentStreak(streak);
          localStorage.setItem("currentDailyStreak", streak.toString());
          
          // Dispatch custom event to notify other components about the streak update
          const event = new CustomEvent("dailyRewardClaimed", { 
            detail: { streak, lastClaim: new Date().toDateString() } 
          });
          window.dispatchEvent(event);
        }

        if (nextTime) {
          setNextRewardTime(nextTime);
        }

        // Play daily reward claim sound
        appSound.playDailyRewardSound();

        toast({
          title: "Daily reward claimed!",
          description: `You've received ${rewardAmount} ${rewardType === "premium" ? "gems" : "coins"}.`,
        });
      },
      onError: (error: any) => {
        if (error.response?.data?.error?.includes("already claimed")) {
          setClaimed(true);

          // Save claimed status to localStorage with today's date
          localStorage.setItem(
            "lastDailyRewardClaim",
            new Date().toDateString(),
          );

          if (error.response?.data?.nextRewardTime) {
            setNextRewardTime(new Date(error.response.data.nextRewardTime));
          }

          toast({
            title: "Already claimed",
            description: "You've already claimed your daily reward today.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description:
              "Failed to claim daily reward. Please try again later.",
            variant: "destructive",
          });
        }
      },
    });
  };

  // Calculate the number of day indicators to display - always show all 30 days
  const getStreakDisplayConfig = () => {
    return {
      totalDays: 30,
      displayText: `Day ${currentStreak}/7 - You're making great progress!`
    };
  };

  // Generate array of 30 days organized in rows of 7 (like a mini calendar)
  const totalDays = 30;
  const daysPerRow = 7;
  const rows = Math.ceil(totalDays / daysPerRow);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative flex gap-1 items-center bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-white border-amber-500 shadow-md hover:shadow-amber-800/30"
        >
          <Gift className="h-4 w-4 text-amber-200" />
          {!claimed && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
              !
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-gray-900 border-0 text-center">
        
        
        <div className="flex items-center justify-center mb-1">
          <Gift className="h-5 w-5 text-amber-400 mr-2" /> {/* Smaller icon here */}
          <DialogTitle className="text-xl font-semibold text-white">Daily Reward</DialogTitle>
        </div>
        
        <DialogDescription className="text-gray-300 mb-8">
          Claim your daily reward to earn coins and gems!
        </DialogDescription>
        
        <div className="flex flex-col items-center justify-center space-y-6">
          {claimed ? (
            <>
              <div className="inline-flex items-center justify-center bg-green-900/30 border-2 border-green-500 rounded-full p-8 mb-2">
                <Gift className="h-10 w-10 text-green-400" />
              </div>
              <div className="text-2xl font-medium text-green-400 mb-2">
                Daily reward claimed!
              </div>
              <div className="text-sm text-gray-300 mb-4">
                Come back tomorrow for your next reward.
              </div>
              <div className="bg-gray-800/40 p-4 rounded-md w-full max-w-xs">
                <div className="flex items-center mb-3">
                  <Gift className="h-4 w-4 text-green-400 mr-2" />
                  <h4 className="text-sm font-medium text-white">Reward Streak</h4>
                </div>
                
                {/* Calendar-style dots display */}
                <div className="flex flex-col gap-2 mb-2">
                  {Array.from({ length: rows }).map((_, rowIndex) => (
                    <div key={rowIndex} className="flex justify-between gap-2">
                      {Array.from({ length: daysPerRow }).map((_, colIndex) => {
                        const dayNumber = rowIndex * daysPerRow + colIndex + 1;
                        if (dayNumber > totalDays) return <div key={colIndex} className="flex-1" />;
                        
                        const isActive = dayNumber <= currentStreak;
                        
                        return (
                          <div 
                            key={colIndex} 
                            className={`h-3 w-3 rounded-full ${
                              isActive 
                                ? "bg-amber-500" 
                                : "bg-gray-700"
                            }`}
                            title={`Day ${dayNumber}`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Day 1</span>
                  <span>Day {totalDays}</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4 bg-gray-800/60 p-3 rounded-md max-w-xs">
                Daily rewards help you collect resources to unlock special items
                in the shop!
              </p>
            </>
          ) : (
            <>
              <div className="bg-gradient-to-br from-amber-900/50 to-amber-800/30 border-2 border-amber-500 p-8 rounded-full mb-2">
                {isLoadingStatus ? (
                  <div className="h-10 w-10 border-t-2 border-b-2 border-amber-400 rounded-full animate-spin" />
                ) : nextReward?.currencyType === "premium" ? (
                  <Gem className="h-10 w-10 text-purple-400" />
                ) : (
                  <Coins className="h-10 w-10 text-amber-400" />
                )}
              </div>
              <h3 className="text-2xl font-medium mb-1 text-white">
                {isLoadingStatus ? (
                  "Loading reward..."
                ) : (
                  <>
                    {nextReward?.amount ?? 50}{" "}
                    {nextReward?.currencyType === "premium" ? "Gems" : "Coins"}
                  </>
                )}
              </h3>
              <p className="text-sm text-gray-300 mb-6">
                Log in daily to earn rewards and build your collection!
              </p>
              <div className="bg-gray-800/40 p-4 rounded-md w-full max-w-xs">
                <div className="flex items-center mb-3">
                  <Gift className="h-4 w-4 text-amber-400 mr-2" />
                  <h4 className="text-sm font-medium text-white">Reward Streak</h4>
                </div>
                
                {/* Calendar-style dots display */}
                <div className="flex flex-col gap-2 mb-2">
                  {Array.from({ length: rows }).map((_, rowIndex) => (
                    <div key={rowIndex} className="flex justify-between gap-2">
                      {Array.from({ length: daysPerRow }).map((_, colIndex) => {
                        const dayNumber = rowIndex * daysPerRow + colIndex + 1;
                        if (dayNumber > totalDays) return <div key={colIndex} className="flex-1" />;
                        
                        const isActive = dayNumber <= currentStreak;
                        const isNext = dayNumber === currentStreak + 1;
                        
                        return (
                          <div 
                            key={colIndex} 
                            className={`h-3 w-3 rounded-full ${
                              isActive
                                ? "bg-amber-500" 
                                : isNext 
                                  ? "bg-amber-800/60" 
                                  : "bg-gray-700"
                            }`}
                            title={`Day ${dayNumber}`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>Day 1</span>
                  <span>Day {totalDays}</span>
                </div>
              </div>
              <Button
                onClick={handleClaimReward}
                disabled={isPending || claimed || isLoadingStatus}
                className="w-full max-w-xs mt-4 bg-gradient-to-r from-amber-700 to-amber-600 hover:from-amber-600 hover:to-amber-500 text-white border-amber-500 shadow-md"
              >
                {isPending ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 border-t-2 border-b-2 border-white rounded-full animate-spin" />
                    <span>Claiming...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Gift className="h-4 w-4" />
                    <span>Claim Daily Reward</span>
                  </div>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
